import React, { useState, useEffect } from 'react';
import { collection, addDoc, serverTimestamp, query, where, getDocs, doc, getDoc, setDoc, increment, deleteDoc } from 'firebase/firestore';
import { signInWithEmailAndPassword, signInWithPopup, GoogleAuthProvider } from 'firebase/auth';
import { auth, db } from '../../firebase/config';
import confetti from 'canvas-confetti';
import './Auth.css';

const Waitlist = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [showMemberLogin, setShowMemberLogin] = useState(false);
  const [memberEmail, setMemberEmail] = useState('');
  const [memberPassword, setMemberPassword] = useState('');
  const [memberLoading, setMemberLoading] = useState(false);
  const [memberError, setMemberError] = useState('');

  // Check if user email is approved (activated from waitlist)
  const checkUserApproved = async (email, uid) => {
    try {
      // If uid provided, check direct user document first
      if (uid) {
        const userDoc = await getDoc(doc(db, 'users', uid));
        if (userDoc.exists()) {
          return { approved: true, reason: 'existing_user' };
        }
      }

      // First check if user exists in users collection (already activated)
      const usersQuery = query(collection(db, 'users'), where('email', '==', email.toLowerCase()));
      const userSnapshot = await getDocs(usersQuery);
      
      if (!userSnapshot.empty) {
        return { approved: true, reason: 'existing_user' };
      }

      // Check if user is in waitlist with activated status
      const waitlistQuery = query(
        collection(db, 'waitlist'), 
        where('email', '==', email.toLowerCase()),
        where('status', '==', 'activated')
      );
      const waitlistSnapshot = await getDocs(waitlistQuery);
      
      if (!waitlistSnapshot.empty) {
        return { approved: true, reason: 'waitlist_approved' };
      }

      return { approved: false, reason: 'not_approved' };
    } catch (error) {
      console.error('Error checking user approval:', error);
      return { approved: false, reason: 'error' };
    }
  };

  // Create user document for approved users on first sign-in
  const createUserDocument = async (user, approval) => {
    try {
      // Check if user document already exists
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      
      if (!userDoc.exists()) {
        // Check if there's a pre-approved user document for this email
        const usersQuery = query(collection(db, 'users'), where('email', '==', user.email.toLowerCase()));
        const userSnapshot = await getDocs(usersQuery);
        
        if (!userSnapshot.empty) {
          // Pre-approved user document exists, copy it to the new UID
          const preApprovedDoc = userSnapshot.docs[0];
          const preApprovedData = preApprovedDoc.data();
          
          // Create user document with the new UID but keep existing data
          await setDoc(doc(db, 'users', user.uid), {
            ...preApprovedData,
            email: user.email, // Ensure email matches
            updatedAt: new Date(),
            firebaseAuthUID: user.uid // Track the actual Firebase Auth UID
          }, { merge: true });
          
          // Delete the old pre-approved document
          await deleteDoc(doc(db, 'users', preApprovedDoc.id));
          
          console.log('Pre-approved user document migrated for:', user.email);
        } else {
          // Create new user document with free plan
          await setDoc(doc(db, 'users', user.uid), {
            email: user.email,
            isSubscribed: true,
            subscriptionType: 'free',
            subscriptionStartDate: new Date(),
            maxAccounts: 1,
            maxVideosPerMonth: 10,
            videosUsedThisMonth: 0,
            currentPeriodStart: new Date(),
            twitterUsernames: [],
            activatedFromWaitlist: approval.reason === 'waitlist_approved',
            createdAt: new Date(),
            updatedAt: new Date(),
            isUnlimited: false,
            totalVideos: 0,
            recentImports: []
          }, { merge: true });
          
          console.log('New user document created for:', user.email);
        }
      }
    } catch (error) {
      console.error('Error creating user document:', error);
    }
  };
  // Launch date: August 15, 2025 at midnight CST
  // CST is UTC-6, so midnight CST is 6 AM UTC
  const launchDate = new Date('2025-08-15T06:00:00Z').getTime();

  // Calculate initial countdown values
  const calculateCountdown = () => {
    const now = new Date().getTime();
    const distance = launchDate - now;

    if (distance > 0) {
      return {
        days: Math.floor(distance / (1000 * 60 * 60 * 24)),
        hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((distance % (1000 * 60)) / 1000)
      };
    } else {
      return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    }
  };

  const [countdown, setCountdown] = useState(calculateCountdown());
  const [waitlistCount, setWaitlistCount] = useState(127); // Start with the inflation base

  // Confetti animation on component mount
  useEffect(() => {
    const duration = 3000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 0 };

    function randomInRange(min, max) {
      return Math.random() * (max - min) + min;
    }

    const interval = setInterval(function() {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        return clearInterval(interval);
      }

      const particleCount = 50 * (timeLeft / duration);

      // Burst from left
      confetti(Object.assign({}, defaults, {
        particleCount,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 }
      }));
      
      // Burst from right
      confetti(Object.assign({}, defaults, {
        particleCount,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 }
      }));
    }, 250);

    return () => clearInterval(interval);
  }, []);

  // Countdown timer effect
  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date().getTime();
      const distance = launchDate - now;

      if (distance > 0) {
        setCountdown({
          days: Math.floor(distance / (1000 * 60 * 60 * 24)),
          hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((distance % (1000 * 60)) / 1000)
        });
      } else {
        setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [launchDate]);

  // Fetch waitlist count from public counter
  useEffect(() => {
    const fetchWaitlistCount = async () => {
      try {
        const counterRef = doc(db, 'public', 'waitlist_counter');
        const counterDoc = await getDoc(counterRef);
        
        if (counterDoc.exists()) {
          const data = counterDoc.data();
          // Add the inflation base to the actual count
          const inflationBase = data.inflationBase || 127;
          setWaitlistCount((data.count || 0) + inflationBase);
        } else {
          // Document doesn't exist, use fallback calculation
          console.log('Counter document not found, using fallback calculation');
          setWaitlistCount(127);
        }
      } catch (error) {
        console.error('Error fetching waitlist count:', error);
        // Calculate a dynamic count based on time if Firebase fails
        const baseCount = 127;
        const startDate = new Date('2024-08-01'); // Adjust to when you started
        const currentDate = new Date();
        const daysSinceStart = Math.floor((currentDate - startDate) / (1000 * 60 * 60 * 24));
        const simulatedGrowth = Math.max(0, Math.floor(daysSinceStart * 3)); // 3 people per day
        
        setWaitlistCount(baseCount + simulatedGrowth);
      }
    };

    fetchWaitlistCount();
  }, []);

  const handleWaitlistSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Add to waitlist - let backend/Firestore handle duplicates
      await addDoc(collection(db, 'waitlist'), {
        email: email.toLowerCase(),
        timestamp: serverTimestamp(),
        source: 'website',
        status: 'pending'
      });

      setSuccess(true);
      setEmail('');
      
      // Update local count immediately
      setWaitlistCount(prev => prev + 1);
      
      // Trigger success confetti
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 }
      });
    } catch (error) {
      console.error('Error adding to waitlist:', error);
      
      // Check if it's a duplicate email error (you can customize this based on your backend)
      if (error.code === 'already-exists' || error.message.includes('already')) {
        setError('You\'re already on our waitlist! We\'ll notify you when access is available.');
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleMemberLogin = async (e) => {
    e.preventDefault();
    setMemberLoading(true);
    setMemberError('');

    try {
      const userCredential = await signInWithEmailAndPassword(auth, memberEmail, memberPassword);
      
      // Check approval and create document if needed
      const approval = await checkUserApproved(userCredential.user.email, userCredential.user.uid);
      if (approval.approved) {
        await createUserDocument(userCredential.user, approval);
      }
      
      // User will be redirected by App.js auth state change
    } catch (error) {
      console.error('Error signing in:', error);
      setMemberError('Invalid email or password. Please try again.');
    } finally {
      setMemberLoading(false);
    }
  };

  const handleGoogleMemberLogin = async () => {
    setMemberLoading(true);
    setMemberError('');
    
    try {
      const provider = new GoogleAuthProvider();
      
      // Configure provider to prevent automatic account creation
      provider.setCustomParameters({
        prompt: 'select_account'
      });

      // First, try to sign in
      try {
        const result = await signInWithPopup(auth, provider);
        const user = result.user;
        
        // Check if this user is approved
        const approval = await checkUserApproved(user.email, user.uid);
        
        if (!approval.approved) {
          // User is not approved, sign them out immediately
          await auth.signOut();
          setMemberError('This Google account is not approved for access. Please join our waitlist first or contact support.');
          setMemberLoading(false);
          return;
        }

        // User is approved, they can proceed
        console.log('Approved user signed in:', user.email);
        // User will be redirected by App.js auth state change
        
        // Create user document if needed
        await createUserDocument(user, approval);
        
      } catch (authError) {
        if (authError.code === 'auth/popup-closed-by-user') {
          setMemberError('Sign in was cancelled.');
        } else if (authError.code === 'auth/account-exists-with-different-credential') {
          setMemberError('An account already exists with this email using a different sign-in method.');
        } else {
          setMemberError('Google sign in failed. Please try again or use email/password.');
        }
      }
      
    } catch (error) {
      console.error('Google member auth error:', error);
      setMemberError('Authentication failed. Please try again.');
    } finally {
      setMemberLoading(false);
    }
  };

  if (success) {
    return (
      <div className="waitlist-main-container">
        <div className="waitlist-success-overlay">
          <div className="waitlist-success-card">
            <div className="waitlist-success-icon">
              <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                <polyline points="22,4 12,14.01 9,11.01"></polyline>
              </svg>
            </div>
            <h1>Welcome to the Future! 🎉</h1>
            <p>You're officially on the waitlist! Get ready to transform tweets into viral videos in seconds. We'll notify you the moment early access goes live!</p>
            <div className="waitlist-success-countdown">
              <h3>Launch in:</h3>
              <div className="countdown-display">
                <div className="countdown-item">
                  <span className="countdown-number">{countdown.days}</span>
                  <span className="countdown-label">Days</span>
                </div>
                <div className="countdown-item">
                  <span className="countdown-number">{countdown.hours}</span>
                  <span className="countdown-label">Hours</span>
                </div>
                <div className="countdown-item">
                  <span className="countdown-number">{countdown.minutes}</span>
                  <span className="countdown-label">Minutes</span>
                </div>
                <div className="countdown-item">
                  <span className="countdown-number">{countdown.seconds}</span>
                  <span className="countdown-label">Seconds</span>
                </div>
              </div>
            </div>
            <button 
              onClick={() => setSuccess(false)} 
              className="waitlist-back-btn"
            >
              Back to Waitlist
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="waitlist-main-container">
      <div className="waitlist-hero-section">
        <div className="waitlist-content">
          <div className="waitlist-logo-section">
            <img src="/logo.png" alt="Twizzler Logo" className="waitlist-logo-image" />
          </div>
          
          <div className="waitlist-main-content">
            <h1 className="waitlist-main-title">
              We are launching in
            </h1>
            
            <div className="countdown-timer">
              <div className="countdown-item">
                <span className="countdown-number">{countdown.days}</span>
                <span className="countdown-label">Days</span>
              </div>
              <div className="countdown-separator">:</div>
              <div className="countdown-item">
                <span className="countdown-number">{countdown.hours}</span>
                <span className="countdown-label">Hours</span>
              </div>
              <div className="countdown-separator">:</div>
              <div className="countdown-item">
                <span className="countdown-number">{countdown.minutes}</span>
                <span className="countdown-label">Minutes</span>
              </div>
              <div className="countdown-separator">:</div>
              <div className="countdown-item">
                <span className="countdown-number">{countdown.seconds}</span>
                <span className="countdown-label">Seconds</span>
              </div>
            </div>

            <p className="waitlist-subtitle">
              Transform tweets into viral videos in 5 seconds. Join thousands waiting for early access to the future of content creation.
            </p>

            <div className="waitlist-form-container">
              <form onSubmit={handleWaitlistSubmit} className="waitlist-form">
                <div className="waitlist-input-group">
                  <input
                    type="email"
                    placeholder="Enter your email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="waitlist-email-input"
                    disabled={loading}
                  />
                  <button 
                    type="submit" 
                    className="waitlist-submit-button"
                    disabled={loading || !email}
                  >
                    {loading ? (
                      <div className="loading-spinner"></div>
                    ) : (
                      'Join Waitlist 🚀'
                    )}
                  </button>
                </div>

                {error && (
                  <div className="waitlist-error">
                    {error}
                  </div>
                )}
              </form>

              <div className="waitlist-stats">
                <div className="stat-item">
                  <span className="stat-number">{waitlistCount.toLocaleString()}</span>
                  <span className="stat-label">People Waiting</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number">5 Sec</span>
                  <span className="stat-label">Video Creation</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number">100%</span>
                  <span className="stat-label">AI Powered</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Member Login Section */}
      <div className="member-login-section">
        <button 
          className="member-login-toggle"
          onClick={() => setShowMemberLogin(!showMemberLogin)}
        >
          Member Login
        </button>

        {showMemberLogin && (
          <div className="member-login-form-container">
            <form className="member-login-form" onSubmit={handleMemberLogin}>
              <h3>Member Login</h3>
              <input
                type="email"
                placeholder="Email"
                value={memberEmail}
                onChange={e => setMemberEmail(e.target.value)}
                className="member-input"
                required
                disabled={memberLoading}
              />
              <input
                type="password"
                placeholder="Password"
                value={memberPassword}
                onChange={e => setMemberPassword(e.target.value)}
                className="member-input"
                required
                disabled={memberLoading}
              />
              <button
                type="submit"
                className="member-login-button"
                disabled={memberLoading}
              >
                {memberLoading ? 'Signing in...' : 'Sign In'}
              </button>
              <button
                type="button"
                className="clerk-social-btn"
                style={{ marginTop: '1rem', background: '#fff', color: '#222', border: '1px solid #eee', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}
                onClick={handleGoogleMemberLogin}
                disabled={memberLoading}
              >
                <svg className="clerk-google-icon" width="20" height="20" viewBox="0 0 48 48"><g><path fill="#4285F4" d="M43.611 20.083H42V20H24v8h11.303C33.973 32.527 29.418 36 24 36c-6.627 0-12-5.373-12-12s5.373-12 12-12c2.73 0 5.23.936 7.217 2.487l6.121-6.121C33.527 6.163 28.977 4 24 4 12.954 4 4 12.954 4 24s8.954 20 20 20c11.046 0 20-8.954 20-20 0-1.341-.138-2.651-.389-3.917z"/><path fill="#34A853" d="M6.306 14.691l6.571 4.819C14.655 16.108 19.008 13 24 13c2.73 0 5.23.936 7.217 2.487l6.121-6.121C33.527 6.163 28.977 4 24 4c-7.732 0-14.41 4.41-17.694 10.691z"/><path fill="#FBBC05" d="M24 44c5.356 0 10.24-1.837 14.055-4.978l-6.484-5.327C29.418 36 24 36 24 36c-5.408 0-9.957-3.463-11.29-8.083l-6.563 5.061C9.57 39.509 16.227 44 24 44z"/><path fill="#EA4335" d="M43.611 20.083H42V20H24v8h11.303c-1.13 3.063-4.303 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c2.73 0 5.23.936 7.217 2.487l6.121-6.121C33.527 6.163 28.977 4 24 4c-7.732 0-14.41 4.41-17.694 10.691l6.571 4.819C14.655 16.108 19.008 13 24 13c2.73 0 5.23.936 7.217 2.487l6.121-6.121C33.527 6.163 28.977 4 24 4c-7.732 0-14.41 4.41-17.694 10.691z"/></g></svg>
                Sign in with Google
              </button>
              {memberError && <div className="member-error">{memberError}</div>}
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default Waitlist; 